import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenStore } from './auth';
import { hydrationSignal } from './hydration';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  // Leave default Content-Type unset so FormData requests get proper multipart
  // boundaries. Axios will set application/json automatically for plain objects.
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _skipAuth?: boolean;
}

function setAuthHeader(cfg: RetriableConfig, token: string): void {
  const headers = (cfg.headers ??= new AxiosHeaders());
  if (headers instanceof AxiosHeaders) {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
}

/**
 * True if the JWT is missing, unparseable, or expires within `skewMs`.
 * Used to refresh proactively (before sending) so a big batch of requests
 * doesn't all fire with a token that expires mid-flight and 401-storm the
 * refresh endpoint. Returns false only when we're confident the token is
 * still good for at least `skewMs`.
 */
function accessTokenExpiringSoon(token: string, skewMs = 60_000): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json) as { exp?: number };
    if (!exp) return false; // no exp claim — don't force refresh, let 401 handle it
    return exp * 1000 - Date.now() <= skewMs;
  } catch {
    return false; // can't decode — leave it to the 401 path
  }
}

api.interceptors.request.use(async (config) => {
  const cfg = config as RetriableConfig;
  if (cfg._skipAuth) return cfg;
  let token = tokenStore.getAccess();
  // Proactively refresh a token that's expired or about to expire, sharing a
  // single in-flight refresh across all concurrent requests. This is what keeps
  // large batch uploads (many files, each 2 API calls) from crossing the token
  // TTL and cascading into "Token expired" failures + forced logout.
  if (token && accessTokenExpiringSoon(token) && tokenStore.getRefresh()) {
    refreshPromise ??= doRefresh().finally(() => {
      refreshPromise = null;
    });
    const refreshed = await refreshPromise;
    if (refreshed) token = refreshed;
  }
  if (token) setAuthHeader(cfg, token);
  return cfg;
});

// De-duplicate concurrent refreshes.
let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  // Wait for the initial page-load hydration to complete first.
  // If hydrate() is already rotating the refresh token, we must not
  // send a concurrent /auth/refresh — the backend revokes on first use.
  await hydrationSignal.promise;
  // After hydration we may already have a fresh access token — but only reuse it
  // if it's actually still valid. Returning an EXPIRED token here (it's still in
  // memory on a 401) was the bug that made batch uploads fail: the "refresh"
  // handed back the dead token, the retry 401'd again, and the session was
  // cleared. Only short-circuit when the token has real life left.
  const hydrated = tokenStore.getAccess();
  if (hydrated && !accessTokenExpiringSoon(hydrated)) return hydrated;
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;
  try {
    const resp = await axios.post(
      `${BASE_URL}/auth/refresh`,
      { refresh_token: refresh },
      { headers: { 'Content-Type': 'application/json' } },
    );
    const { access_token, refresh_token } = resp.data as {
      access_token: string;
      refresh_token?: string;
    };
    tokenStore.setAccess(access_token);
    if (refresh_token) tokenStore.setRefresh(refresh_token);
    return access_token;
  } catch {
    tokenStore.clear();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (!original || original._retried || original._skipAuth) {
      return Promise.reject(error);
    }
    const status = error.response?.status;
    if (status === 401) {
      original._retried = true;
      refreshPromise ??= doRefresh().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        const headers = (original.headers ??= new AxiosHeaders());
        if (headers instanceof AxiosHeaders) {
          headers.set('Authorization', `Bearer ${newToken}`);
        } else {
          (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        }
        return api.request(original);
      }
      // refresh failed — kick to login, but never redirect away from public pages
      const PUBLIC_PATHS = ['/login', '/password-reset', '/accept-invite', '/mfa-setup-required', '/session-expired', '/auth/sso-callback'];
      const isPublic = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
      if (typeof window !== 'undefined' && !isPublic) {
        const from = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?from=${from}`);
      }
    }
    return Promise.reject(error);
  },
);

export function apiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    return err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

export type { AxiosRequestConfig };
